import { observer } from 'mobx-react-lite';

import _ from 'lodash';
import { makeAutoObservable, runInAction } from 'mobx';
import { useEffect } from 'react';
import { useParams } from 'react-router';
import { StockIndexResponse, StockResponse } from '../../../response/responseSchema';
import modalStore from '@client/store/modalStore';
import Box from '@client/uikit/Box';
import Frame from '@client/uikit/Frame';
import { apiClient } from '@app/apiClient';
import { useMobxInstance } from '@client/clientUtils/mobxUtil';
import ChangeBarGraph, { ChangeBarGraphItem } from './ChangeBarGraph';
import TargetReturnButton from './TargetReturnButton';

class ViewStore {
    indexHoldings: StockResponse[] = [];
    indexPerformance: StockIndexResponse[] = [];
    currentIndex: StockIndexResponse | null = null;
    briefDate: string | null = null;
    targetReturn: 'daily' | 'mtd' | 'ytd' = 'daily';

    graphItems: ChangeBarGraphItem<{ ticker: string }>[] = [];

    isLoading: boolean = true;

    constructor() {
        makeAutoObservable(this);
    }

    async fetch(indexTicker: string) {
        runInAction(() => {
            this.isLoading = true;
        });

        try {
            const [indexHoldings, indexPerformance] = await Promise.all([
                apiClient.stockIndex.public.getIndexHoldings.$query({ indexTicker }),
                apiClient.stockIndex.public.getIndexPerformance.$query(),
            ]);

            runInAction(() => {
                this.indexHoldings = indexHoldings as StockResponse[];
                this.indexPerformance = indexPerformance;
                this.briefDate = _.last(indexPerformance)?.price?.date || null;

                this.makeGraphItems(indexTicker);
            });
        } finally {
            runInAction(() => {
                this.isLoading = false;
            });
        }
    }

    private makeGraphItems(indexTicker: string) {
        // S&P 500 변화율 추출
        const sp500Index = this.indexPerformance.find((index) => index.ticker === '_SP500');
        let sp500Return = 0;
        if (this.targetReturn === 'daily') {
            sp500Return = Number(sp500Index?.price?.dailyReturn) || 0;
        } else if (this.targetReturn === 'mtd') {
            sp500Return = Number(sp500Index?.price?.mtdReturn) || 0;
        } else if (this.targetReturn === 'ytd') {
            sp500Return = Number(sp500Index?.price?.ytdReturn) || 0;
        }

        // 현재 인덱스 변화율 추출
        this.currentIndex =
            this.indexPerformance.find((index) => index.ticker === indexTicker) || null;
        let currentIndexReturn = 0;
        if (this.targetReturn === 'daily') {
            currentIndexReturn = Number(this.currentIndex?.price?.dailyReturn) || 0;
        } else if (this.targetReturn === 'mtd') {
            currentIndexReturn = Number(this.currentIndex?.price?.mtdReturn) || 0;
        } else if (this.targetReturn === 'ytd') {
            currentIndexReturn = Number(this.currentIndex?.price?.ytdReturn) || 0;
        }

        this.graphItems = _.uniqBy(
            _.compact([
                ...this.indexHoldings.map((holding) => {
                    let targetReturn = 0;
                    if (this.targetReturn === 'daily') {
                        targetReturn = Number(holding.price?.dailyReturn) || 0;
                    } else if (this.targetReturn === 'mtd') {
                        targetReturn = Number(holding.price?.mtdReturn) || 0;
                    } else if (this.targetReturn === 'ytd') {
                        targetReturn = Number(holding.price?.ytdReturn) || 0;
                    }

                    return {
                        name: holding.ticker,
                        changePercent: targetReturn,
                        data: {
                            ticker: holding.ticker,
                        },
                    };
                }),
                {
                    name: 'S&P500',
                    changePercent: sp500Return,
                    isBenchmark: true,
                },
                {
                    name: this.currentIndex?.nameKo || '',
                    changePercent: currentIndexReturn,
                    isBenchmark: true,
                },
            ]),
            'name',
        );
    }

    changeTargetReturn(target: 'daily' | 'mtd' | 'ytd', indexTicker: string) {
        this.targetReturn = target;
        this.makeGraphItems(indexTicker);
    }

    get highlightPercent() {
        return this.targetReturn === 'daily' ? 3 : this.targetReturn === 'mtd' ? 5 : 10;
    }
}

const IndexHoldingsPage = observer(() => {
    const { indexTicker } = useParams<{ indexTicker: string }>();
    const viewStore = useMobxInstance(ViewStore);

    useEffect(() => {
        if (indexTicker) {
            viewStore.fetch(indexTicker);
        }
    }, [viewStore, indexTicker]);

    return (
        <Frame>
            <Frame.Title
                title={viewStore.currentIndex?.nameKo + ' 지수'}
                isLoading={viewStore.isLoading}
            />
            <Box isLoading={viewStore.isLoading}>
                <Box.Title title={'시가총액 상위 10개 종목'} />
                <ChangeBarGraph<{ ticker: string }>
                    items={viewStore.graphItems}
                    highligthPercent={viewStore.highlightPercent}
                    className="-mx-5"
                    isShowArrow={(item) => {
                        return !item.isBenchmark;
                    }}
                    onClickBar={(item) => {
                        if (item.isBenchmark) {
                            return;
                        }

                        const ticker = item.data?.ticker;
                        if (ticker) {
                            modalStore.haewao.stock({
                                ticker,
                            });
                        }
                    }}
                />
                {viewStore.briefDate && indexTicker && (
                    <TargetReturnButton
                        className="mt-8"
                        targetDate={viewStore.briefDate}
                        targetReturn={viewStore.targetReturn}
                        onChangeTargetReturn={(target) =>
                            viewStore.changeTargetReturn(target, indexTicker)
                        }
                    />
                )}
            </Box>
        </Frame>
    );
});

export default IndexHoldingsPage;
